<template>
  <!--
    【P249】全平台统一消息面板（销售 / 市场 / 财务 / 运营 四端共用）

    统一的能力（源自销售端，已抽象）：
      · 三/四 Tab 分类（由父级通过 tabs 传入，顺序与文案可自定义）
      · 红色气泡角标（仅 count > 0 时显示）
      · 批量条（全选 + 勾选 + 「批量已读」/「批量删除」/「批量确认」）
      · 未读红点、标签、时间
      · 描述完整展示（★ 不加 line-clamp / text-overflow，多行自适应）
      · 底部「去处理」入口（showAction 时显示，点击 emit action）
      · 批量二次确认用自研 ConfirmDialog 组件（不用原生 $confirm）
      · 手机端易操作：Tab 等宽铺满、触摸区 ~46px、吸顶、按压反馈

    父级只需传：tabs / list / counts / tab 当前值，并监听事件。
  -->
  <div class="mp">
    <!-- 顶部条（可选，运营/市场/财务都有） -->
    <div class="mp-top" v-if="showBack" @click="$emit('back')">
      <van-icon name="arrow-left" class="mp-top__back" />
      <span class="mp-top__t">{{ title }}</span>
      <span class="mp-top__r" @click.stop="$emit('readAll')">{{ readAllText }}</span>
    </div>

    <!-- 分类 Tab：等宽铺满，手机端友好 -->
    <div class="mp-tabs" v-if="tabs.length > 1">
      <div v-for="t in tabs" :key="t.value"
           class="mp-tab" :class="{ 'mp-tab--on': tab === t.value }"
           @click="onSwitchTab(t.value)">
        <span class="mp-tab__tx">{{ t.label }}</span>
        <em class="mp-tab__dot" v-if="t.count > 0">{{ t.count > 99 ? '99+' : t.count }}</em>
      </div>
    </div>

    <!-- 批量二次确认（全站统一弹窗） -->
    <ConfirmDialog
      v-model="dialogConfirm"
      :title="confirmTitle"
      :desc="confirmDesc"
      :loading="busy"
      :loading-text="busyText"
      :confirm-text="confirmBtnText"
      @confirm="doBatch"
    />

    <!-- 批量操作条 -->
    <div class="mp-batch" v-if="batchMode && list.length">
      <label class="mp-batch__all">
        <input type="checkbox" :checked="pickedAll" @change="onToggleAll" />
        <span>全选</span>
      </label>
      <span class="mp-batch__n">已选 <b>{{ picked.length }}</b> / {{ list.length }} 条</span>
      <button class="mp-batch__btn" :disabled="!picked.length || busy" @click="onAskBatch">
        {{ busy ? busyText : batchBtnText }}
      </button>
    </div>

    <!-- 列表 -->
    <van-pull-refresh :value="refreshing" @input="onRefreshing" @refresh="$emit('refresh')">
      <div class="mp-list">
        <div v-if="err" class="mp-err">{{ err }}</div>

        <div class="mp-card" v-for="m in list" :key="keyOf(m)" @click="$emit('open', m)">
          <label class="mp-card__pick" v-if="batchMode" @click.stop>
            <input type="checkbox" :checked="isPicked(m)" @change="onToggleOne(m)" />
          </label>
          <!-- 未读红点 -->
          <span class="mp-card__dot" :class="{ 'mp-card__dot--on': !isRead(m) }"></span>
          <div class="mp-card__body">
            <div class="mp-card__top">
              <span class="mp-card__tag" :class="tagClass(m)">{{ roleText(m) }}</span>
              <span class="mp-card__scene">{{ sceneText(m) }}</span>
              <span class="mp-card__time">{{ timeText(m) }}</span>
            </div>
            <!-- ★ 描述完整展示：white-space:pre-wrap，不限行数 -->
            <div class="mp-card__text">{{ textOf(m) }}</div>
            <!-- 底部「去处理」 -->
            <div class="mp-card__act" v-if="showAction" @click.stop="$emit('action', m)">
              <span>去处理</span>
              <van-icon name="arrow" class="mp-card__act-ico" />
            </div>
          </div>
        </div>

        <div class="mp-empty" v-if="!loading && !list.length">
          <div class="mp-empty__i">{{ emptyIcon }}</div>
          <div class="mp-empty__t">{{ err || emptyText }}</div>
        </div>
        <div class="mp-empty" v-if="loading"><div class="mp-empty__t">加载中…</div></div>
      </div>
    </van-pull-refresh>

    <div class="mp-foot" v-if="list.length">
      <span>{{ footHint }}</span>
      <span>共 {{ list.length }} 条</span>
    </div>
  </div>
</template>

<script>
import ConfirmDialog from './ConfirmDialog.vue'

/* 消息 scene → 中文名（各端共用一张表，避免各端各写一份） */
const SCENE = {
  room_order_todo: '会议室待办', room_order_change: '订单变更',
  room_order_cancel: '已取消', room_order_cc: '抄送', room_order_finish: '已完成',
  approval: '审批', payment: '回款', refund: '退款', contract: '合同',
  order: '订单', customer: '客户', opportunity: '商机',
}
const EMPTY = {
  pending: '没有待处理的消息',
  confirmed: '还没有已确认的记录',
  unread: '没有未读消息',
  cc: '暂无抄送',
  all: '暂无消息',
  reject: '没有待阅驳回的消息',
}

export default {
  name: 'MessagePanel',
  components: { ConfirmDialog },
  props: {
    title:        { type: String,  default: '消息' },
    showBack:     { type: Boolean, default: true },
    readAllText:  { type: String,  default: '全部已读' },
    /* [{ value, label, count }] —— 顺序/文案/数量全由父级控制 */
    tabs:         { type: Array,   default: () => [] },
    tab:          { type: String,  default: '' },
    list:         { type: Array,   default: () => [] },
    loading:      { type: Boolean, default: false },
    refreshing:   { type: Boolean, default: false },
    busy:         { type: Boolean, default: false },
    err:          { type: String,  default: '' },
    /* 批量：enabled 是否显示批量条；action = read|delete|confirm */
    batchEnabled: { type: Boolean, default: false },
    batchAction:  { type: String,  default: 'read' },   // read | delete | confirm
    /* 底部「去处理」入口 */
    showAction:   { type: Boolean, default: false },
    actionText:   { type: String,  default: '去处理' },
    emptyIcon:    { type: String,  default: '✉' },
  },
  data() {
    return { picked: [], dialogConfirm: false, refreshingProxy: this.refreshing }
  },
  computed: {
    batchMode()   { return this.batchEnabled && this.list.length > 0; },
    pickedAll()   {
      const n = this.list.length;
      return n > 0 && new Set(this.picked).size === n;
    },
    batchBtnText() {
      return { read: '批量已读', delete: '批量删除', confirm: '批量确认' }[this.batchAction] || '批量处理';
    },
    busyText() {
      return { read: '处理中', delete: '删除中', confirm: '处理中' }[this.batchAction] || '处理中';
    },
    confirmTitle() {
      return {
        read:    '确认标记已读',
        delete:  '确认彻底删除',
        confirm: '确认批量确认',
      }[this.batchAction] || '请确认';
    },
    confirmBtnText() {
      return { read: '标记已读', delete: '删除', confirm: '确认' }[this.batchAction] || '确定';
    },
    confirmDesc() {
      const n = this.picked.length;
      if (this.batchAction === 'delete') {
        return '确定彻底删除所选的 ' + n + ' 条消息吗？删除后不可恢复。';
      }
      if (this.batchAction === 'confirm') {
        return '确定将所选的 ' + n + ' 条消息标记为已确认吗？确认后将同步处理对应单据。';
      }
      return '确定将所选的 ' + n + ' 条消息标记为已读吗？';
    },
    emptyText() {
      return EMPTY[this.tab] || '暂无消息';
    },
    footHint() {
      if (this.batchAction === 'confirm' && this.tab === 'pending') return '勾选后点「批量确认」';
      if (this.batchAction === 'delete') return '勾选后可批量删除（不可恢复）';
      return '';
    },
  },
  watch: {
    /* 切 Tab / 换数据 / 换分类都清空勾选，防跨 Tab 误操作 */
    tab()        { this.picked = []; },
    list()       { this.picked = []; },
    batchEnabled(){ this.picked = []; },
  },
  methods: {
    keyOf(m)     { return m.sn || m.id; },
    isRead(m)    { return String(m.is_read) === '1' || Number(m.is_read) === 1; },
    isPicked(m)  { return this.picked.indexOf(String(this.keyOf(m))) >= 0; },
    roleText(m)  {
      if (Number(m.role) === 2) return '抄送';
      return m.role_name || (Number(m.type) === 2 ? '驳回' : '待办');
    },
    tagClass(m)  { return Number(m.role) === 2 ? 'mp-card__tag--cc' : 'mp-card__tag--todo'; },
    sceneText(m) { return SCENE[m.scene] || m.scene || ''; },
    timeText(m)  { return (m.create_time || '').slice(5, 16); },
    textOf(m)    { return m.content || (m.item && m.item.content) || ''; },
    onRefreshing(v){ this.refreshingProxy = v; },

    onSwitchTab(v)   { if (v !== this.tab) { this.$emit('tab', v); this.picked = []; } },
    onToggleOne(m)   {
      const k = String(this.keyOf(m)), i = this.picked.indexOf(k);
      if (i >= 0) this.picked.splice(i, 1); else this.picked.push(k);
    },
    onToggleAll(e)   {
      const on = e && e.target && e.target.checked;
      this.picked = on ? Array.from(new Set(this.list.map(m => String(this.keyOf(m))))) : [];
    },
    onAskBatch() {
      if (!this.picked.length) { this.$toast && this.$toast('请先勾选'); return; }
      this.dialogConfirm = true;
    },
    doBatch() {
      const sns = this.picked.slice();
      this.$emit('batch', { action: this.batchAction, sns });
    },
  },
}
</script>

<style scoped>
.mp { display:flex; flex-direction:column; min-height:100%; background:#f5f6f8; }

/* 顶部条 */
.mp-top{ display:flex; align-items:center; gap:10px; height:44px; padding:0 14px;
  background:#fff; border-bottom:.5px solid #ebedf0; flex:0 0 auto; }
.mp-top__back{ font-size:18px; color:#323233; }
.mp-top__t{ font-size:15px; font-weight:500; color:#1f2328; }
.mp-top__r{ margin-left:auto; font-size:12px; color:#2b5fd9; }

/* 分类 Tab：等宽铺满 + 触摸区 ~46px + 吸顶 */
.mp-tabs{ display:flex; background:#fff; border-bottom:.5px solid #ebedf0;
  position:sticky; top:0; z-index:6; flex:0 0 auto; }
.mp-tab{ position:relative; flex:1 1 0; min-width:0; padding:13px 2px 11px;
  text-align:center; border-bottom:2.5px solid transparent;
  -webkit-tap-highlight-color:transparent; }
.mp-tab:active{ background:#f2f3f5; }
.mp-tab--on{ border-bottom-color:#2b5fd9; }
.mp-tab__tx{ font-size:14px; font-weight:500; color:#8b949e; white-space:nowrap; }
.mp-tab--on .mp-tab__tx{ color:#2b5fd9; font-weight:600; }
.mp-tab__dot{ position:absolute; top:7px; left:calc(50% + 20px);
  min-width:17px; height:17px; padding:0 5px; border-radius:9px;
  background:#d93026; color:#fff; font-size:10.5px; line-height:17px;
  text-align:center; font-style:normal; box-sizing:border-box; }

/* 批量条 */
.mp-batch{ display:flex; align-items:center; gap:10px; margin:10px 12px; padding:9px 11px;
  background:#eef1f5; border-radius:9px; flex:0 0 auto; }
.mp-batch__all{ display:inline-flex; align-items:center; gap:6px; font-size:13px; color:#1f2328; }
.mp-batch__all input, .mp-card__pick input{ width:16px; height:16px; accent-color:#2b5fd9; }
.mp-batch__n{ font-size:12px; color:#5b636b; margin-left:auto; }
.mp-batch__n b{ color:#1f2328; }
.mp-batch__btn{ flex:0 0 auto; padding:7px 15px; border:none; border-radius:7px;
  background:#2b5fd9; color:#fff; font-size:12.5px; }
.mp-batch__btn:disabled{ background:#c9cdd4; }

/* 列表 */
.mp-list{ padding:10px 12px 0; }
.mp-err{ margin-bottom:8px; padding:8px 10px; border-radius:8px;
  background:#fef0f0; color:#a12a20; font-size:12px; }
.mp-card{ display:flex; gap:9px; padding:12px 13px; margin-bottom:8px;
  background:#fff; border:1px solid #ebedf0; border-radius:10px; }
.mp-card__pick{ flex:0 0 auto; display:flex; align-items:flex-start; padding-top:2px; }
.mp-card__dot{ flex:0 0 auto; width:7px; height:7px; border-radius:50%;
  margin-top:5px; background:transparent; }
.mp-card__dot--on{ background:#d93026; }
.mp-card__body{ flex:1; min-width:0; }
.mp-card__top{ display:flex; align-items:center; gap:6px; margin-bottom:4px; }
.mp-card__tag{ flex:0 0 auto; padding:1px 6px; border-radius:4px;
  font-size:10.5px; line-height:1.6; }
.mp-card__tag--todo{ background:#FAEEDA; color:#854F0B; }
.mp-card__tag--cc{ background:#EAF3DE; color:#3B6D11; }
.mp-card__scene{ font-size:10.5px; color:#8b949e; }
.mp-card__time{ font-size:10.5px; color:#8b949e; margin-left:auto; flex:0 0 auto; }
/* ★ 描述完整展示：pre-wrap 多行自适应，不加 line-clamp / ellipsis */
.mp-card__text{ font-size:12.5px; line-height:1.65; color:#1f2328;
  white-space:pre-wrap; word-break:break-word; overflow:visible; }
/* 底部「去处理」 */
.mp-card__act{ display:inline-flex; align-items:center; gap:3px; margin-top:9px;
  padding:5px 11px; border-radius:14px; background:#eef2fb; color:#2b5fd9;
  font-size:12px; font-weight:600; }
.mp-card__act-ico{ font-size:12px; }

.mp-empty{ padding:40px 0 50px; text-align:center; }
.mp-empty__i{ font-size:26px; color:#c9cdd4; margin-bottom:8px; }
.mp-empty__t{ font-size:12.5px; color:#8b949e; }
.mp-foot{ display:flex; justify-content:space-between; padding:12px 16px 18px;
  font-size:11.5px; color:#8b949e; }
</style>



// WEBPACK FOOTER //
// src/components/MessagePanel.vue